import { Stack, Typography, Tab, Tabs, Box, Button } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import "./MyProfile.scss";
import MyPersonalData from "../../../MyPersonalData/MyPersonalData";
import MyPassword from "../../../MyPassword/MyPassword";
import MyPasswordFeedbackMessage from "../../../MyPasswordFeedbackMessage/MyPasswordFeedbackMessage";
import Contact from "../../../Contact/Contact";
import { TabsDataMyProfile } from "../../../../core/models/myProfile/myProfile.model";
import { logoutGygia } from "../../../../core/services/gygia/gygia.service";
import { setEntitySelectionToLocalStorage } from "../../../../core/services/localStorage/localStorage.service";

interface MyProfileProps {
  isDesktopViewport: boolean;
  handleClose: () => void;
}

interface MyProfileWrapperProps {
  isDesktopViewport: boolean;
  isMenuOpen: boolean;
  wrapper: (element: JSX.Element) => JSX.Element;
  children: JSX.Element;
}

const MyProfileWrapper = ({
  isDesktopViewport,
  isMenuOpen,
  wrapper,
  children,
}: MyProfileWrapperProps) =>
  isDesktopViewport ? (
    wrapper(children)
  ) : (
    <Box
      component="article"
      className={`my-profile ${isMenuOpen && "my-profile--active"}`}
    >
      {children}
    </Box>
  );

const MyProfile = ({ isDesktopViewport, handleClose }: MyProfileProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMyPasswordSuccessfull, setIsMyPasswordSuccessfull] =
    useState<boolean>(false);
  const [myProfileTabSelected, setMyProfileTabSelected] = useState<
    string | boolean
  >(false);

  const tabsDataMyProfile: TabsDataMyProfile[] = useMemo(
    () => [
      // {
      //   id: "myPersonalData",
      //   label: "Datos Personales",
      //   value: "myPersonalData",
      //   href: process.env.REACT_APP_PROFILE!,
      //   icon: "t-icon-user",
      // },
      // {
      //   id: "myPassword",
      //   label: "Contraseña",
      //   value: "myPassword",
      //   href: process.env.REACT_APP_CHANGE_PASSWORD!,
      //   icon: "t-icon-lock",
      // },
      {
        id: "contact",
        label: "Contacto",
        value: "contact",
        href: process.env.REACT_APP_CONTACT!,
        icon: "t-icon-phone",
      },
    ],
    []
  );

  useEffect(() => {
    const tab = tabsDataMyProfile.find(
      ({ href }) => location.pathname === href
    );

    if (tab) {
      setMyProfileTabSelected(tab.value);
    }
  }, [location.pathname, tabsDataMyProfile]);

  const renderContent = () => {
    switch (myProfileTabSelected) {
      case "myPersonalData":
        return <MyPersonalData handleGoBackClick={handleGoBackClick} />;
      case "myPassword":
        if (isMyPasswordSuccessfull) {
          return (
            <MyPasswordFeedbackMessage
              handleClick={() => {
                setIsMyPasswordSuccessfull(false);
                handleClose();
              }}
            />
          );
        }
        return (
          <MyPassword
            handleGoBackClick={handleGoBackClick}
            setIsMyPasswordSuccessfull={setIsMyPasswordSuccessfull}
          />
        );
      case "contact":
        return <Contact handleGoBackClick={handleGoBackClick} />;
      default:
        return <></>;
    }
  };

  const userLogout = async (e: { preventDefault: () => void }) => {
    localStorage.clear();
    sessionStorage.clear();
    await logoutGygia();
    clearCookies();
    clearIndexedDB();
    clearCacheStorage();
    setEntitySelectionToLocalStorage(null);
  };

  const clearCookies = () => {
    const cookies = document.cookie.split(";");

    cookies.forEach((cookie) => {
      const [name] = cookie.split("=");
      document.cookie = `${name.trim()}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    });
  };

  const clearIndexedDB = () => {
    if (window.indexedDB && window.indexedDB.databases) {
      window.indexedDB.databases().then((databases) => {
        databases.forEach((db) => {
          window.indexedDB.deleteDatabase(db?.name || "");
        });
      });
    }
  };

  const clearCacheStorage = () => {
    if ("caches" in window) {
      caches.keys().then((keyList) => {
        return Promise.all(keyList.map((key) => caches.delete(key)));
      });
    }
  };

  const handleClick = (href: string) => {
    if (isDesktopViewport) {
      navigate(href!);
      handleClose();
    }
  };

  const handleGoBackClick = () => {
    setMyProfileTabSelected(false);
  };

  if (myProfileTabSelected && !isDesktopViewport) {
    return renderContent();
  }

  return (
    <>
      <Stack height="100%">
        <Stack
          color="#374649"
          width={{ xs: "85%", md: "375px" }}
          padding="1.5rem"
        >
          <Stack alignSelf="center" alignItems="start" gap="1.375rem">
            <Typography variant="subtitle1" fontFamily="Nunito">
              {t("Header.Nav.Perfil")}
            </Typography>
            <Typography variant="body1" fontFamily="Nunito">
              {t("Header.Perfil.Descripción")}
            </Typography>
          </Stack>
        </Stack>
        <Stack>
          <Tabs
            className="profile-tabs"
            TabIndicatorProps={{ style: { backgroundColor: "#ED0000" } }}
            value={myProfileTabSelected}
            onChange={(event, newValue) => {
              setMyProfileTabSelected(newValue);
            }}
            component={"ul"}
            aria-label="nav tabs example"
            orientation={"vertical"}
            sx={{
              margin: "0",
              ".MuiTabs-indicator": {
                left: 0,
              },
            }}
          >
            {tabsDataMyProfile.map(({ id, label, value, href, icon }) => (
              <Tab
                key={id}
                component="a"
                label={
                  <Typography variant="body1" component="p" fontFamily="Nunito">
                    <span
                      className={`${icon} brand-red`}
                      style={{
                        paddingLeft: "1.5rem",
                        paddingRight: "15px",
                        fontSize: "18px",
                      }}
                    />
                    {t(`Header.Perfil.${label}`)}
                  </Typography>
                }
                onClick={() => handleClick(href!)}
                value={value}
                className="tab"
                disableRipple
              />
            ))}
          </Tabs>
        </Stack>
        <Stack padding="1.5rem" justifyContent="end" flex={0.8}>
          <Button
            onClick={userLogout}
            type="button"
            disableRipple
            sx={{
              padding: 0,
              justifyContent: "start",
              ":focus": {
                backgroundColor: "unset",
              },
              ":hover": {
                backgroundColor: "unset",
              },
            }}
            style={{ padding: 0 }}
          >
            <span
              className="t-icon-power-off"
              style={{
                fontSize: "18px",
                paddingRight: "15px",
              }}
            />
            <Typography
              component="p"
              className="brand-gray"
              textTransform="none"
              fontFamily="Nunito"
            >
              {t("Header.Perfil.Desconectar")}
            </Typography>
          </Button>
        </Stack>
      </Stack>
    </>
  );
};

export { MyProfile, MyProfileWrapper };
